import React, { useEffect, useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { useSearch } from '@/contexts/SearchContext';
import { useAuth } from '@/contexts/AuthContext';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { hospitalAdminApi } from '@/api/hospitalAdmin';
import { Department } from '@/types/types';

export default function DepartmentManagement() {
    const { searchQuery } = useSearch();
    const { isChainAdmin, user, activeHospitalId } = useAuth();
    const chainHospitals = user?.hospitals ?? [];
    const [selectedHospitalId, setSelectedHospitalId] = useState('');
    const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
    const [departmentName, setDepartmentName] = useState('');
    const [departmentDescription, setDepartmentDescription] = useState('');

    useEffect(() => {
        const fallback =
            activeHospitalId || user?.hospitalId || chainHospitals[0]?.id || '';
        setSelectedHospitalId((prev) => prev || fallback);
    }, [activeHospitalId, user?.hospitalId, chainHospitals]);

    const { data: departments, isLoading, refetch } = useQuery<Department[]>({
        queryKey: ['departments', selectedHospitalId],
        queryFn: () =>
            hospitalAdminApi.getDepartments(
                selectedHospitalId ? { hospitalId: selectedHospitalId } : undefined
            ),
        enabled: !!selectedHospitalId || !isChainAdmin,
    });

    const addDepartmentMutation = useMutation({
        mutationFn: hospitalAdminApi.createDepartment,
        onSuccess: (result) => {
            const sync =
                result &&
                typeof result === 'object' &&
                'department' in result &&
                result.department
                    ? result
                    : null;
            if (sync && 'created' in sync) {
                toast.success(
                    sync.created > 0
                        ? `Department synced across chain (${sync.created} created, ${sync.skipped} already existed)`
                        : 'Department already exists in all chain hospitals'
                );
            } else {
                toast.success('Department added successfully');
            }
            setIsAddDialogOpen(false);
            setDepartmentName('');
            setDepartmentDescription('');
            refetch();
        },
        onError: (error: any) => {
            toast.error(error.response?.data?.message || 'Failed to add department');
        },
    });

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (!departmentName.trim()) {
            toast.error('Department name is required');
            return;
        }
        if (isChainAdmin && !selectedHospitalId) {
            toast.error('Select a hospital');
            return;
        }
        addDepartmentMutation.mutate({
            name: departmentName.trim(),
            description: departmentDescription.trim(),
            hospitalId: selectedHospitalId || undefined,
        });
    };

    if (isLoading) {
        return <div>Loading departments...</div>;
    }

    const filteredDepartments = departments?.filter(department =>{
        if(!searchQuery) {  
        return true;
    }
        return department.name.toLowerCase().includes(searchQuery.toLowerCase())
    });
    return (
        <div className="container mx-auto py-10">
            <div className="flex justify-between items-center mb-6 gap-4 flex-wrap">
                <h1 className="text-2xl font-bold">Department Management</h1>
                <div className="flex items-center gap-3 flex-wrap">
                    {isChainAdmin && chainHospitals.length > 0 && (
                        <Select value={selectedHospitalId} onValueChange={setSelectedHospitalId}>
                            <SelectTrigger className="w-[220px]">
                                <SelectValue placeholder="Select hospital" />
                            </SelectTrigger>
                            <SelectContent>
                                {chainHospitals.map((h) => (
                                    <SelectItem key={h.id} value={h.id}>
                                        {h.name}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    )}
                    <Button
                        onClick={() => setIsAddDialogOpen(true)}
                        className="bg-blue-800 text-white hover:bg-blue-700"
                    >
                        Add Department
                    </Button>
                </div>
            </div>

            {isChainAdmin && (
                <p className="mb-4 text-sm text-muted-foreground">
                    New departments are automatically created in every hospital in your chain when the name does not already exist there.
                </p>
            )}

            <Table>
                <TableHeader>
                    <TableRow>
                        <TableHead>Department Name</TableHead>
                        <TableHead>Created At</TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {filteredDepartments?.map((department) => (
                        <TableRow key={department.id}>
                            <TableCell>{department.name}</TableCell>
                            <TableCell>
                                {new Date(department.createdAt).toLocaleDateString()}
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>

            <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
                <DialogContent className="sm:max-w-[500px]">
                    <DialogHeader>
                        <DialogTitle>Add New Department</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleSubmit} className="space-y-4">
                        {isChainAdmin && chainHospitals.length > 0 && (
                            <div className="space-y-2">
                                <Label>Hospital</Label>
                                <Select value={selectedHospitalId} onValueChange={setSelectedHospitalId}>
                                    <SelectTrigger>
                                        <SelectValue placeholder="Select hospital" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {chainHospitals.map((h) => (
                                            <SelectItem key={h.id} value={h.id}>
                                                {h.name}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                                <p className="text-xs text-muted-foreground">
                                    This department will be added to all hospitals in the chain.
                                </p>
                            </div>
                        )}
                        <div className="space-y-2">
                            <Label htmlFor="name">Department Name</Label>
                            <Input
                                id="name"
                                value={departmentName}
                                onChange={(e) => setDepartmentName(e.target.value)}
                                placeholder="Enter department name"
                                required
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="description">Description</Label>
                            <Input
                                id="description"
                                value={departmentDescription}
                                onChange={(e) => setDepartmentDescription(e.target.value)}
                                placeholder="Enter department description"
                            />
                        </div>
                        <div className="flex justify-end gap-2">
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => setIsAddDialogOpen(false)}
                            >
                                Cancel
                            </Button>
                            <Button
                                type="submit"
                                disabled={addDepartmentMutation.isPending}
                                className="bg-blue-800 text-white hover:bg-blue-700"
                            >
                                {addDepartmentMutation.isPending ? 'Adding...' : 'Add Department'}
                            </Button>
                        </div>
                    </form>
                </DialogContent>
            </Dialog>
        </div>
    );
}
